import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown, faCheckCircle, faRocket, faGift,
  faArrowRight, faClock,
  faArrowLeft, faShieldAlt, faSpinner, faPercent, faCheck, faTicketAlt, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import Heatmap from '../components/Heatmap';
import { getExamUrl } from '../utils/slugify';

interface Exam {
  id: number;
  name: string;
  description: string;
  is_free_available: boolean;
}

interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription: {
    id: number;
    start_date: string;
    end_date: string;
  } | null;
}

interface PricingPlan {
  period_days: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
}

const SubscriptionPage: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exam, setExam] = useState<Exam | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [validatedCoupon, setValidatedCoupon] = useState<{ code: string; discount_percent: number; description?: string } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [examId]);

  const fetchData = async () => {
    try {
      const [examRes, statusRes, pricingRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/subscriptions/${examId}/status`),
        api.get(`/subscriptions/pricing`)
      ]);

      setExam(examRes.data.exam);
      setSubscriptionStatus(statusRes.data);
      // Filter out 60-day plan and sort: 30, 90, 365 (optimal order)
      const plans = (pricingRes.data.pricing || [])
        .filter((plan: PricingPlan) => plan.period_days !== 60)
        .sort((a: PricingPlan, b: PricingPlan) => {
          const order = [30, 90, 365];
          return order.indexOf(a.period_days) - order.indexOf(b.period_days);
        });
      setPricingPlans(plans);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (priceCents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(priceCents / 100);
  };

  const calculateSavings = (plan: PricingPlan) => {
    // Calculate savings compared to 30-day plan
    const monthlyPlan = pricingPlans.find(p => p.period_days === 30);
    if (!monthlyPlan || plan.period_days === 30) return null;
    
    const monthlyPricePerDay = monthlyPlan.price_cents / 30;
    const equivalentPrice = monthlyPricePerDay * plan.period_days;
    const savings = equivalentPrice - plan.price_cents;
    const savingsPercent = Math.round((savings / equivalentPrice) * 100);
    
    return savingsPercent > 0 ? savingsPercent : null;
  };

  const getPlanLabel = (days: number) => {
    if (days === 365) return '1 Year';
    if (days === 60) return '2 Months';
    if (days === 90) return '3 Months';
    return `${days} Days`;
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      setValidatingCoupon(true);
      setCouponError(null);
      const response = await api.post('/coupons/validate', { code: couponCode.trim() });
      
      if (response.data.valid) {
        setValidatedCoupon(response.data.coupon);
        setCouponError(null);
      } else {
        setValidatedCoupon(null);
        setCouponError(response.data.error || 'Invalid coupon code');
      }
    } catch (error: any) {
      setValidatedCoupon(null);
      setCouponError(error.response?.data?.error || 'Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setValidatedCoupon(null);
    setCouponError(null);
  };

  const handleSubscribe = async (days: number) => {
    try {
      setProcessing(true);
      setSelectedPlan(days);
      const response = await api.post(`/subscriptions/${examId}/checkout`, { 
        days,
        coupon_code: validatedCoupon?.code || null
      });

      if (response.data.url) {
        // If mock payment, redirect directly to success page
        if (response.data.mock) {
          window.location.href = response.data.url;
        } else {
          // Real Stripe checkout - redirect to Stripe
          window.location.href = response.data.url;
        }
      }
    } catch (error: any) {
      console.error('Failed to create checkout:', error);
      alert(error.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const calculateDiscountedPrice = (plan: PricingPlan) => {
    if (!validatedCoupon) return plan.price_cents;
    const discount = Math.round(plan.price_cents * (validatedCoupon.discount_percent / 100));
    return Math.max(0, plan.price_cents - discount);
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="text-sm gp-text-muted">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="gp-page flex items-center justify-center">
        <p className="text-sm gp-text-muted">Exam not found</p>
      </div>
    );
  }

  const hasActive = subscriptionStatus?.hasActiveSubscription;
  const subscription = subscriptionStatus?.subscription;

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        <button
          type="button"
          onClick={() => exam && navigate(getExamUrl(parseInt(examId!), exam.name))}
          className="gp-back-link mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" aria-hidden="true" />
          Back to Exam
        </button>

        <div className="gp-page-header text-center">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-3"
            style={{ background: 'linear-gradient(135deg, var(--gp-accent) 0%, #d97706 100%)', color: '#fff' }}
          >
            <FontAwesomeIcon icon={faCrown} className="text-xl" aria-hidden="true" />
          </div>
          <h1 className="gp-page-title">
            Premium Access - {exam.name}
          </h1>
          {exam.description && (
            <p className="gp-page-subtitle max-w-2xl mx-auto">{exam.description}</p>
          )}
        </div>

        {hasActive && subscription && (
          <div
            className="gp-card mb-5 text-center"
            style={{
              background: 'linear-gradient(135deg, var(--gp-success) 0%, #059669 100%)',
              border: 'none',
              color: '#fff'
            }}
          >
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl" aria-hidden="true" />
            </div>
            <h3 className="text-lg font-bold mb-1">Premium Active</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.9)' }}>
              Your premium access is active until{' '}
              <strong>
                {new Date(subscription.end_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </strong>
            </p>
            <button
              type="button"
              onClick={() => exam && navigate(getExamUrl(parseInt(examId!), exam.name))}
              className="inline-flex items-center gap-2 font-semibold py-2 px-5 rounded-md text-sm transition-all"
              style={{ background: '#fff', color: 'var(--gp-success)' }}
            >
              <FontAwesomeIcon icon={faRocket} className="text-sm" aria-hidden="true" />
              <span>Start Practicing</span>
            </button>
          </div>
        )}

        {!hasActive && (
          <>
            <div className="gp-card mb-5 max-w-2xl mx-auto">
              <div className="flex items-center gap-2 mb-3">
                <FontAwesomeIcon icon={faTicketAlt} style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                <h3 className="text-base font-semibold" style={{ color: 'var(--gp-ink)' }}>Have a Coupon Code?</h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError(null);
                      if (validatedCoupon) setValidatedCoupon(null);
                    }}
                    placeholder="Enter coupon code"
                    className="input-field"
                    style={
                      validatedCoupon
                        ? { borderColor: 'var(--gp-success)' }
                        : couponError
                        ? { borderColor: '#ef4444' }
                        : undefined
                    }
                    disabled={validatingCoupon}
                  />
                </div>
                {validatedCoupon ? (
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="gp-btn-ghost"
                    style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                  >
                    <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                    <span>Remove</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleValidateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="gp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {validatingCoupon ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                        <span>Validating...</span>
                      </>
                    ) : (
                      <>
                        <span>Apply</span>
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </>
                    )}
                  </button>
                )}
              </div>
              {validatedCoupon && (
                <div
                  className="mt-3 p-3 rounded-md"
                  style={{ background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)' }}
                >
                  <div className="flex items-center gap-2" style={{ color: 'var(--gp-success)' }}>
                    <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                    <span className="font-semibold text-sm">
                      {validatedCoupon.code} - {validatedCoupon.discount_percent}% off applied!
                    </span>
                  </div>
                  {validatedCoupon.description && (
                    <p className="text-xs mt-1" style={{ color: 'var(--gp-success)' }}>{validatedCoupon.description}</p>
                  )}
                </div>
              )}
              {couponError && (
                <div className="gp-alert-error mt-3 mb-0">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                    <span className="text-sm">{couponError}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="text-center mb-5">
                <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--gp-ink)' }}>
                  Choose Your Plan
                </h2>
                <p className="text-sm gp-text-muted">
                  Unlock unlimited access to premium questions and advanced features
                </p>
              </div>

              {pricingPlans.length > 0 ? (
                <div className={`grid gap-3 ${pricingPlans.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : pricingPlans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} max-w-5xl mx-auto`}>
                  {pricingPlans.map((plan) => {
                    const isYearly = plan.period_days === 365;
                    const savings = calculateSavings(plan);
                    const isProcessing = processing && selectedPlan === plan.period_days;
                    const discountedPrice = calculateDiscountedPrice(plan);
                    const hasDiscount = validatedCoupon && discountedPrice < plan.price_cents;
                    const discountAmount = plan.price_cents - discountedPrice;
                    
                    return (
                      <div
                        key={plan.period_days}
                        className={`gp-pricing-card ${isYearly ? 'gp-pricing-card-popular' : ''}`}
                      >
                        {isYearly && (
                          <div className="gp-pricing-badge">Best Value</div>
                        )}
                        
                        <div className="text-center mb-4 pt-1">
                          <h3 className="text-base font-bold mb-2" style={{ color: 'var(--gp-ink)' }}>
                            {getPlanLabel(plan.period_days)}
                          </h3>
                          <div className="mb-2">
                            {hasDiscount && (
                              <div className="text-sm gp-text-muted line-through mb-0.5">
                                {formatPrice(plan.price_cents, plan.currency)}
                              </div>
                            )}
                            <div>
                              <span className="text-lg gp-text-muted">$</span>
                              <span className="text-3xl font-bold" style={{ color: 'var(--gp-ink)' }}>
                                {(discountedPrice / 100).toFixed(2)}
                              </span>
                            </div>
                            {hasDiscount && (
                              <div className="mt-1 text-xs font-semibold" style={{ color: 'var(--gp-success)' }}>
                                Save {formatPrice(discountAmount, plan.currency)} ({validatedCoupon?.discount_percent}% off)
                              </div>
                            )}
                          </div>
                          {savings && savings > 0 && !hasDiscount && (
                            <div
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                              style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                            >
                              <FontAwesomeIcon icon={faPercent} className="text-xs" aria-hidden="true" />
                              <span>Save {savings}%</span>
                            </div>
                          )}
                        </div>

                        <ul className="space-y-2 mb-5 text-sm flex-1">
                          {[
                            'Unlimited premium questions',
                            'Full exam mode access',
                            'Detailed analytics',
                            `${plan.period_days} days full access`
                          ].map((feature) => (
                            <li key={feature} className="flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
                              <span
                                className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                                style={{ background: 'var(--gp-success-soft)' }}
                              >
                                <FontAwesomeIcon icon={faCheck} className="text-[10px]" style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                              </span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          type="button"
                          onClick={() => handleSubscribe(plan.period_days)}
                          className={`w-full ${isYearly ? 'gp-btn-accent' : 'gp-btn-ghost'}`}
                          disabled={processing}
                        >
                          {isProcessing ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" aria-hidden="true" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <span>Subscribe</span>
                              <FontAwesomeIcon icon={faArrowRight} className="text-xs" aria-hidden="true" />
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="gp-card text-center py-8">
                  <p className="text-sm gp-text-muted">No pricing plans available at the moment.</p>
                </div>
              )}
            </div>

            {exam.is_free_available && (
              <div className="gp-soft-panel text-center p-5 mb-5 relative overflow-hidden">
                <div className="relative z-10 py-2">
                  <div className="gp-icon-tile mx-auto mb-3">
                    <FontAwesomeIcon icon={faGift} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--gp-ink)' }}>Free Tier Available</h3>
                  <p className="text-sm gp-text-muted mb-4 max-w-2xl mx-auto">
                    You can still access free questions for this exam. Premium subscription unlocks
                    all questions and advanced features.
                  </p>
                  <button
                    type="button"
                    onClick={() => exam && navigate(getExamUrl(parseInt(examId!), exam.name))}
                    className="gp-btn-ghost text-sm inline-flex"
                  >
                    <span>Try Free Questions</span>
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-5 grid md:grid-cols-3 gap-3 max-w-3xl mx-auto">
              <div className="text-center">
                <FontAwesomeIcon icon={faShieldAlt} className="text-xl mb-1.5" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                <p className="text-xs gp-text-muted font-medium">Secure Payment</p>
              </div>
              <div className="text-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-xl mb-1.5" style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                <p className="text-xs gp-text-muted font-medium">Instant Access</p>
              </div>
              <div className="text-center">
                <FontAwesomeIcon icon={faClock} className="text-xl mb-1.5" style={{ color: 'var(--gp-accent)' }} aria-hidden="true" />
                <p className="text-xs gp-text-muted font-medium">Full Year Access</p>
              </div>
            </div>
          </>
        )}

        {hasActive && <Heatmap examId={parseInt(examId!)} />}
      </div>
    </div>
  );
};

export default SubscriptionPage;
